import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// `withCredentials: true` (iteration 154 auth hardening) — the backend now
// issues a HttpOnly session cookie on login. Axios must be told to attach
// that cookie on every subsequent request, otherwise we'd fall through to
// the deprecated Authorization: Bearer path forever.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Legacy bearer fallback — keep sending Authorization header if we still
  // have a token in localStorage. Backend accepts BOTH cookie and bearer
  // during the 30-day migration window. New logins will overwrite the
  // localStorage token AND set the cookie, so this stays in lock-step.
  const t = localStorage.getItem("bvs_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Gateway-level hiccups (Cloudflare 52x / 502-504 / dropped connections)
// are retried transparently for idempotent GETs — the user should never
// see "origin sent a response Cloudflare could not parse" for a poll.
const TRANSIENT = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530]);
const RETRY_DELAYS_MS = [400, 1200, 3000];
export const isTransientError = (err) =>
  !!err?.config && !err?.response?.data?.detail && (!err.response || TRANSIENT.has(err.response.status));

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    if (isTransientError(err) && (cfg.method || "get").toLowerCase() === "get" && !cfg.__noRetry) {
      const n = cfg.__retry || 0;
      if (n < RETRY_DELAYS_MS.length) {
        cfg.__retry = n + 1;
        await new Promise((res) => setTimeout(res, RETRY_DELAYS_MS[n]));
        return api.request(cfg);
      }
    }
    if (err.response?.status === 401 && !window.location.pathname.startsWith("/sign/")) {
      // Only boot to /login on genuine CRM-session failures. Third-party
      // upstream auth issues (Xero, Resend, etc.) can also return 401 from
      // OUR backend, and we don't want those to log the user out.
      const detail = String(err.response?.data?.detail || "").toLowerCase();
      const url = String(err.config?.url || "").toLowerCase();
      const isCrmAuth = (
        detail.includes("not authenticated") ||
        detail.includes("invalid token") ||
        detail.includes("user not found") ||
        url.includes("/auth/")
      );
      if (isCrmAuth) {
        localStorage.removeItem("bvs_token");
        localStorage.removeItem("bvs_user");
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

// Convenience helper — clears the server-side cookie AND wipes local state.
// Any Logout button should call this instead of manually clearing localStorage.
export async function logout() {
  try {
    await api.post("/auth/logout");
  } catch {
    // Non-fatal — even if the request fails (offline, expired), we still
    // want to wipe local state so the user leaves the app.
  }
  localStorage.removeItem("bvs_token");
  localStorage.removeItem("bvs_user");
}
